import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../../config/api'
import axios from 'axios'
import html2pdf from 'html2pdf.js'
import * as XLSX from 'xlsx-js-style'
import Topbar from '../../mini-comp/Topbar'
import Sidebar from '../../mini-comp/Sidebar'
import {
  RiArrowUpSLine,
  RiArrowDownSLine,
  RiCalendarLine,
  RiMoneyDollarCircleLine,
  RiWallet3Line,
  RiBankCardLine,
  RiPercentLine,
  RiArrowRightSLine,
  RiLoader4Line,
  RiErrorWarningLine,
  RiDownload2Line,
  RiFileList3Line,
  RiFileExcel2Line,
  RiFilePdfLine
} from 'react-icons/ri'

const Reports = () => {
  const navigate = useNavigate()
  const reportRef = useRef(null)
  const tableRef = useRef(null)
  const [summaryData, setSummaryData] = useState(null)
  const [payrollRuns, setPayrollRuns] = useState([])
  const [selectedRunId, setSelectedRunId] = useState('')
  const [payslips, setPayslips] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingSchedules, setLoadingSchedules] = useState(false)
  const [error, setError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic_salary')

  const company_name = Cookies.get('company_name')

  // Default to current month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  })

  const fetchSummary = useCallback(async (month) => {
    setLoading(true)
    setError(null)
    try {
      const token = Cookies.get('token')
      // Fetch summary
      const summaryRes = await axios.get(`${API_BASE_URL}/api/v1/payroll/summary/?month=${month}-01`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      setSummaryData(summaryRes.data)

      // Fetch all runs
      const runsRes = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      setPayrollRuns(runsRes.data)

      // Find the run for this month and set it as default
      const currentRun = runsRes.data.find(run => run.month.startsWith(month))
      if (currentRun) {
        setSelectedRunId(currentRun.id)
      } else {
        setSelectedRunId('')
        setPayslips([])
      }
    } catch (err) {
      console.error('Error fetching payroll data:', err)
      setError('Failed to load payroll data. Please ensure payroll has been run for this period.')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPayslips = useCallback(async (runId) => {
    if (!runId) return
    setLoadingSchedules(true)
    try {
      const token = Cookies.get('token')
      const payslipsRes = await axios.get(`${API_BASE_URL}/api/v1/payroll/runs/${runId}/payslips/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      setPayslips(Array.isArray(payslipsRes.data) ? payslipsRes.data : [])
    } catch (err) {
      console.error('Error fetching payslips:', err)
    } finally {
      setLoadingSchedules(false)
    }
  }, [])

  useEffect(() => {
    fetchSummary(selectedMonth)
  }, [selectedMonth, fetchSummary])

  useEffect(() => {
    if (selectedRunId) {
      fetchPayslips(selectedRunId)
    }
  }, [selectedRunId, fetchPayslips])

  const handleDownloadPDF = async () => {
    const element = reportRef.current
    if (!element) return

    setDownloading(true)

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Payroll_Report_${selectedMonth}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        backgroundColor: '#ffffff',
        scrollY: 0,
        scrollX: 0
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }

    try {
      element.style.position = 'static'
      element.style.left = '0'
      element.style.display = 'block'
      element.style.visibility = 'visible'
      element.style.opacity = '1'

      await html2pdf().set(opt).from(element).save()

      element.style.position = 'absolute'
      element.style.left = '-9999px'
      element.style.display = 'none'
    } catch (err) {
      console.error('PDF generation error:', err)
    } finally {
      setDownloading(false)
    }
  }

  const safeNum = (val) => {
    if (!val && val !== 0) return 0
    const parsed = parseFloat(String(val).replace(/,/g, ''))
    return isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100
  }

  const getRowData = (ps) => {
    const ssrNo = ps.employee_data?.nssa_ssr_number || 'N/A'
    let employer = 0
    let employee = 0
    let total = 0

    switch (activeTab) {
      case 'basic_salary':
        employer = safeNum(ps.employee_data?.basic_salary)
        total = employer
        break
      case 'gross_income':
        employer = safeNum(ps.gross_salary)
        total = employer
        break
      case 'taxable_income':
        employer = safeNum(ps.taxable_income)
        total = employer
        break
      case 'nssa':
        employer = safeNum(ps.nssa)
        employee = safeNum(ps.nssa)
        total = employer + employee
        break
      case 'paye':
        employer = safeNum(ps.paye)
        total = employer
        break
      case 'aids_levy':
        employer = safeNum(ps.aids_levy)
        total = employer
        break
      case 'net_pay':
        employer = safeNum(ps.net_salary)
        total = employer
        break
      case 'leave':
        employer = safeNum(ps.employee_data?.vacation_leave_days_available || ps.leave_balance_bf)
        employee = safeNum(ps.employee_data?.vacation_leave_accrual_rate || ps.leave_accrued)
        total = safeNum(ps.employee_data?.leave_taken || ps.leave_taken)
        break
      default:
        break
    }

    return {
      id: ps.employee_data?.employee_id,
      name: (ps.employee_data?.first_name || '') + ' ' + (ps.employee_data?.last_name || ''),
      ssr: ssrNo,
      employer,
      employee,
      total,
      balance: activeTab === 'leave' ? Math.round((employer) * 100) / 100 : 0,
      isLeave: activeTab === 'leave'
    }
  }

  // Applies thin all-around borders to every cell in the data region of a worksheet.
  // headerRows: number of non-data rows at the top to skip (title/period lines).
  const applyBordersToDataRange = (ws, headerRows = 0) => {
    const border = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
    const ref = ws['!ref']
    if (!ref) return
    const range = XLSX.utils.decode_range(ref)
    for (let R = range.s.r + headerRows; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddr = XLSX.utils.encode_cell({ r: R, c: C })
        if (!ws[cellAddr]) {
          ws[cellAddr] = { t: 's', v: '' }
        }
        ws[cellAddr].s = { ...(ws[cellAddr].s || {}), border }
      }
    }
  }

  const handleExportExcel = () => {
    if (payslips.length === 0) return

    const tabLabel = tabs.find(t => t.id === activeTab)?.label || activeTab
    const runInfo = payrollRuns.find(r => r.id === selectedRunId)
    const runDate = runInfo ? new Date(runInfo.month).toLocaleString('en-US', { month: 'long', year: 'numeric' }) : selectedMonth
    const isLeave = activeTab === 'leave'

    const wb = XLSX.utils.book_new()

    if (activeTab === 'allowances') {
      const allowanceGroups = getAllowanceGroups(payslips)
      const groupTitles = Object.keys(allowanceGroups)

      if (groupTitles.length === 0) return

      groupTitles.forEach(title => {
        const groupData = allowanceGroups[title]
        const total = groupData.reduce((sum, item) => sum + item.amount, 0)

        const exportRows = groupData.map(item => ({
          'Emp No.': item.id,
          'Full Name': item.name,
          'SSRNo.': item.ssr,
          'Employer Amount': item.amount,
          'Employee Amount': '-',
          'Total Amount': item.amount
        }))

        exportRows.push({
          'Emp No.': 'TOTAL',
          'Full Name': '',
          'SSRNo.': '',
          'Employer Amount': total,
          'Employee Amount': '-',
          'Total Amount': total
        })

        const ws = XLSX.utils.json_to_sheet([])
        // Title starts at B1 — column A is intentionally left blank
        XLSX.utils.sheet_add_aoa(ws, [
          [`${company_name || 'Burray HR'}`],
          [`ALLOWANCE SCHEDULE: ${title.toUpperCase()}`],
          [`Period: ${runDate}`],
          [], // blank row after title
          []  // extra spacer before headers
        ], { origin: 'B1' })
        // Data starts at B6 (5 title/spacer rows + 1-indexed)
        XLSX.utils.sheet_add_json(ws, exportRows, { origin: 'B6', skipHeader: false })
        // Set column widths — col A is the empty left margin
        ws['!cols'] = [
          { wch: 4 },   // A – empty margin
          { wch: 12 },  // Emp No.
          { wch: 30 },  // Full Name
          { wch: 20 },  // SSRNo.
          { wch: 20 },  // Employer Amount
          { wch: 20 },  // Employee Amount
          { wch: 20 },  // Total Amount
        ]
        // Apply borders to data rows only (skip 5 title/spacer rows)
        applyBordersToDataRange(ws, 5)
        XLSX.utils.book_append_sheet(wb, ws, title.substring(0, 31)) // Sheet name max 31 chars
      })
    } else {
      const dataToExport = payslips.map(ps => {
        const row = getRowData(ps)
        if (isLeave) {
          return {
            'Emp No.': row.id,
            'Full Name': row.name,
            'SSRNo.': row.ssr,
            'Balance b/f': row.employer,
            'Leave Accrued': row.employee,
            'Leave Taken': row.total,
            'Balance': row.balance
          }
        }
        return {
          'Emp No.': row.id,
          'Full Name': row.name,
          'SSRNo.': row.ssr,
          'Employer Amount': row.employer,
          'Employee Amount': row.employee,
          'Total Amount': row.total
        }
      })

      // Calculate totals
      const totalsRaw = payslips.reduce((acc, ps) => {
        const row = getRowData(ps)
        acc.employer += row.employer
        acc.employee += row.employee
        acc.total += row.total
        acc.balance += (row.balance || 0)
        return acc
      }, { employer: 0, employee: 0, total: 0, balance: 0 })
      const totals = {
        employer: Math.round(totalsRaw.employer * 100) / 100,
        employee: Math.round(totalsRaw.employee * 100) / 100,
        total: Math.round(totalsRaw.total * 100) / 100,
        balance: Math.round(totalsRaw.balance * 100) / 100
      }

      if (isLeave) {
        dataToExport.push({
          'Emp No.': 'TOTAL',
          'Full Name': '',
          'SSRNo.': '',
          'Balance b/f': totals.employer,
          'Leave Accrued': totals.employee,
          'Leave Taken': totals.total,
          'Balance': totals.balance
        })
      } else {
        dataToExport.push({
          'Emp No.': 'TOTAL',
          'Full Name': '',
          'SSRNo.': '',
          'Employer Amount': totals.employer,
          'Employee Amount': totals.employee,
          'Total Amount': totals.total
        })
      }

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet([])

      // Title starts at B1 — column A is intentionally left blank
      XLSX.utils.sheet_add_aoa(ws, [
        [`${company_name || 'Burray HR'}`],
        [`PAYROLL SCHEDULE: ${tabLabel.toUpperCase()}`],
        [`Period: ${runDate}`],
        [], // blank row after title
        []  // extra spacer before headers
      ], { origin: 'B1' })

      // Data starts at B6 (5 title/spacer rows + 1-indexed)
      XLSX.utils.sheet_add_json(ws, dataToExport, { origin: 'B6', skipHeader: false })

      // Set column widths — col A is the empty left margin
      if (isLeave) {
        ws['!cols'] = [
          { wch: 4 },   // A – empty margin
          { wch: 12 },  // Emp No.
          { wch: 30 },  // Full Name
          { wch: 20 },  // SSRNo.
          { wch: 16 },  // Balance b/f
          { wch: 16 },  // Leave Accrued
          { wch: 16 },  // Leave Taken
          { wch: 16 },  // Balance
        ]
      } else {
        ws['!cols'] = [
          { wch: 4 },   // A – empty margin
          { wch: 12 },  // Emp No.
          { wch: 30 },  // Full Name
          { wch: 20 },  // SSRNo.
          { wch: 20 },  // Employer Amount
          { wch: 20 },  // Employee Amount
          { wch: 20 },  // Total Amount
        ]
      }

      // Apply borders to data rows only (skip 5 title/spacer rows)
      applyBordersToDataRange(ws, 5)
      XLSX.utils.book_append_sheet(wb, ws, tabLabel)
    }

    XLSX.writeFile(wb, `Payroll_Schedule_${tabLabel}_${selectedMonth}.xlsx`)
  }

  const handleExportTablePDF = async () => {
    const element = tableRef.current
    if (!element) return

    const tabLabel = tabs.find(t => t.id === activeTab)?.label || activeTab
    const runInfo = payrollRuns.find(r => r.id === selectedRunId)
    const runDate = runInfo ? new Date(runInfo.month).toLocaleString('en-US', { month: 'long', year: 'numeric' }) : selectedMonth

    // Create a temporary container for PDF generation to include the heading
    const tempContainer = document.createElement('div')
    tempContainer.style.padding = '20px'
    tempContainer.style.backgroundColor = '#ffffff'

    const heading = document.createElement('div')
    heading.innerHTML = `
      <div style="text-align: center; margin-bottom: 20px; padding-bottom: 10px;">
        <h1 style="font-size: 23px; color: #0d6efd; margin-bottom: 5px;">${company_name || 'Burray HR'}</h1>
        <h1 style="font-size: 16px; color: #0d6efd; margin-bottom: 5px;">Payroll Schedule(${runDate}): ${tabLabel.toUpperCase()}</h1>
      </div>
    `

    const tableClone = element.cloneNode(true)
    tempContainer.appendChild(heading)
    tempContainer.appendChild(tableClone)

    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Payroll_Schedule_${tabLabel}_${selectedMonth}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
    }

    try {
      await html2pdf().set(opt).from(tempContainer).save()
    } catch (err) {
      console.error('Table PDF generation error:', err)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0)
  }

  const renderTrend = (comparison) => {
    if (!comparison || comparison.percentage_change === undefined) return null

    const percentage = comparison.percentage_change
    const difference = comparison.difference
    const isPositive = percentage > 0
    const isNeutral = percentage === 0

    return (
      <div className="text-end">
        <div className={`d-flex align-items-center justify-content-end gap-1 ${isNeutral ? 'text-muted' : isPositive ? 'text-danger' : 'text-success'}`} style={{ fontSize: '0.85rem' }}>
          {isNeutral ? null : isPositive ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
          <span className="fw-bold">{Math.abs(percentage).toFixed(1)}%</span>
          {!isNeutral && (
            <span className="small fw-medium" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>
              {isPositive ? 'Increase' : 'Decrease'}
            </span>
          )}
        </div>
        {difference !== undefined && difference !== 0 && (
          <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
            {difference > 0 ? '+' : ''}{formatCurrency(difference)} vs last month
          </div>
        )}
      </div>
    )
  }

  const SummaryCard = ({ title, value, comparison, icon: Icon, color }) => (
    <div className="card border-0 shadow-sm h-100 mb-4">
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className={`p-3 rounded-3 bg-${color}-subtle text-${color}`}>
            <Icon size={24} />
          </div>
          {renderTrend(comparison)}
        </div>
        <div>
          <p className="text-muted mb-1 fw-medium">{title}</p>
          <h3 className="mb-0 fw-bold">{typeof value === 'number' ? formatCurrency(value) : value}</h3>
        </div>
      </div>
    </div>
  )

  const tabs = [
    { id: 'basic_salary', label: 'Basic Salaries' },
    { id: 'gross_income', label: 'Gross Income' },
    { id: 'taxable_income', label: 'Taxable Income' },
    { id: 'nssa', label: 'NSSA' },
    { id: 'paye', label: 'PAYE' },
    { id: 'aids_levy', label: 'AIDS Levy' },
    { id: 'net_pay', label: 'Net Pay' },
    { id: 'leave', label: 'Leave' },
    { id: 'allowances', label: 'Allowances' }
  ]

  const getAllowanceGroups = (payslips) => {
    const groups = {}
    payslips.forEach(ps => {
      const allowances = ps.allowance_data || []
      allowances.forEach(allowance => {
        const title = allowance.title || 'Other Allowance'
        if (!groups[title]) {
          groups[title] = []
        }
        groups[title].push({
          id: ps.employee_data?.employee_id,
          name: (ps.employee_data?.first_name || '') + ' ' + (ps.employee_data?.last_name || ''),
          ssr: ps.employee_data?.nssa_ssr_number || 'N/A',
          amount: safeNum(allowance.amount)
        })
      })
    })
    return groups
  }

  const renderTable = () => {
    if (loadingSchedules) {
      return (
        <div className="text-center py-5">
          <RiLoader4Line className="spinner text-primary mb-3" size={32} />
          <p className="text-muted">Loading schedule data...</p>
        </div>
      )
    }

    if (payslips.length === 0) {
      return (
        <div className="text-center py-5 text-muted">
          No schedule data available for this period.
        </div>
      )
    }

    const isLeave = activeTab === 'leave'
    const commonCols = isLeave
      ? ['Emp No.', 'Full Name', 'SSRNo.', 'Balance b/f', 'Leave Accrued', 'Leave Taken', 'Balance']
      : ['Emp No.', 'Full Name', 'SSRNo.', 'Employer Amount', 'Employee Amount', 'Total Amount']

    // Calculate totals
    const totalsRaw = payslips.reduce((acc, ps) => {
      const row = getRowData(ps)
      acc.employer += row.employer
      acc.employee += row.employee
      acc.total += row.total
      acc.balance += (row.balance || 0)
      return acc
    }, { employer: 0, employee: 0, total: 0, balance: 0 })
    const totals = {
      employer: Math.round(totalsRaw.employer * 100) / 100,
      employee: Math.round(totalsRaw.employee * 100) / 100,
      total: Math.round(totalsRaw.total * 100) / 100,
      balance: Math.round(totalsRaw.balance * 100) / 100
    }

    if (activeTab === 'allowances') {
      const allowanceGroups = getAllowanceGroups(payslips)
      const groupTitles = Object.keys(allowanceGroups)

      if (groupTitles.length === 0) {
        return (
          <div className="text-center py-5 text-muted">
            No allowance data available for this period.
          </div>
        )
      }

      return (
        <div ref={tableRef}>
          {groupTitles.map((title, gIdx) => {
            const groupData = allowanceGroups[title]
            const total = groupData.reduce((sum, item) => sum + item.amount, 0)

            return (
              <div key={gIdx} className="mb-5 px-4 pt-4">
                <h5 className="fw-bold text-primary mb-3 text-uppercase">{title}</h5>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 border">
                    <thead className="bg-light-subtle">
                      <tr>
                        <th className="ps-4 py-3 text-uppercase small fw-bold text-muted">Emp No.</th>
                        <th className="py-3 text-uppercase small fw-bold text-muted">Full Name</th>
                        <th className="py-3 text-uppercase small fw-bold text-muted">SSR No.</th>
                        <th className="pe-4 py-3 text-uppercase small fw-bold text-muted text-end">Employer Amount</th>
                        <th className="pe-4 py-3 text-uppercase small fw-bold text-muted text-end">Employee Amount</th>
                        <th className="pe-4 py-3 text-uppercase small fw-bold text-muted text-end">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupData.map((item, idx) => (
                        <tr key={idx}>
                          <td className="ps-4 fw-medium">{item.id}</td>
                          <td>{item.name}</td>
                          <td>{item.ssr}</td>
                          <td className="pe-4 text-end fw-bold text-dark">{formatCurrency(item.amount)}</td>
                          <td className="pe-4 text-end fw-bold text-dark">-</td>
                          <td className="pe-4 text-end fw-bold text-dark">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-light-subtle border-top">
                      <tr className="fw-bold">
                        <td className="ps-4 py-3" colSpan="3">TOTAL {title.toUpperCase()}</td>
                        <td className="pe-4 text-end text-primary">{formatCurrency(total)}</td>
                        <td className="pe-4 text-end text-primary">-</td>
                        <td className="pe-4 text-end text-primary">{formatCurrency(total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    return (
      <div className="table-responsive" ref={tableRef}>
        <table className="table table-hover align-middle mb-0">
          <thead className="bg-dark-subtle">
            <tr>
              {commonCols.map((col, idx) => (
                <th key={idx} className={`${idx === 0 ? 'ps-4' : ''} ${idx === commonCols.length - 1 ? 'pe-4' : ''} py-3 text-uppercase small fw-bold text-muted ${idx >= 3 ? 'text-end' : ''}`}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payslips.map((ps, index) => {
              const data = getRowData(ps)
              return (
                <tr key={index}>
                  <td className="ps-4 fw-medium">{data.id}</td>
                  <td>{data.name}</td>
                  <td>{data.ssr}</td>
                  <td className="text-end">{data.isLeave ? parseFloat(data.employer).toFixed(2) : formatCurrency(data.employer)}</td>
                  <td className="text-end">{data.isLeave ? parseFloat(data.employee).toFixed(2) : formatCurrency(data.employee)}</td>
                  <td className={data.isLeave ? "text-end" : "pe-4 text-end fw-bold text-primary"}>
                    {data.isLeave ? parseFloat(data.total).toFixed(2) : formatCurrency(data.total)}
                  </td>
                  {data.isLeave && (
                    <td className="pe-4 text-end fw-bold text-primary">
                      {parseFloat(data.balance).toFixed(2)}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
          <tfoot className="bg-dark-subtle border-top-2">
            <tr className="fw-bold">
              <td className="ps-4 py-3">TOTAL</td>
              <td></td>
              <td></td>
              <td className="text-end">{activeTab === 'leave' ? parseFloat(totals.employer).toFixed(2) : formatCurrency(totals.employer)}</td>
              <td className="text-end">{activeTab === 'leave' ? parseFloat(totals.employee).toFixed(2) : formatCurrency(totals.employee)}</td>
              <td className={activeTab === 'leave' ? "text-end" : "pe-4 text-end text-primary"}>
                {activeTab === 'leave' ? parseFloat(totals.total).toFixed(2) : formatCurrency(totals.total)}
              </td>
              {activeTab === 'leave' && (
                <td className="pe-4 text-end text-primary">
                  {parseFloat(totals.balance).toFixed(2)}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    )
  }

  return (
    <div className="app-container">
      <Topbar />
      <Sidebar />

      <div className="app-content-area">
        <div className="app-content-wrap p-4">
          <div className="container-fluid p-0">
            {/* Header Section */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
              <div>
                <h2 className="fw-bold mb-0">Payroll Summary Dashboard</h2>
                <p className="text-muted mb-0">Visual analysis of your payroll expenditures and obligations for {selectedMonth}.</p>
              </div>

              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-primary d-flex align-items-center gap-2 px-4 shadow-sm border-0"
                  onClick={handleDownloadPDF}
                  disabled={downloading || loading || !summaryData}
                  style={{ borderRadius: '10px', height: '48px' }}
                >
                  {downloading ? (
                    <RiLoader4Line className="spinner" />
                  ) : (
                    <RiDownload2Line size={20} />
                  )}
                  <span className="fw-bold">{downloading ? 'Generating...' : 'Download PDF'}</span>
                </button>

                <div className="d-flex align-items-center bg-white border rounded-3 p-1 shadow-sm" style={{ height: '48px' }}>
                  <div className="px-3 py-2 border-end d-none d-sm-block text-muted">
                    <RiCalendarLine />
                  </div>
                  <input
                    type="month"
                    className="form-control border-0 shadow-none py-2 px-3"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    style={{ width: '160px', cursor: 'pointer', background: 'transparent' }}
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
                <RiLoader4Line className="spinner text-primary mb-3" size={48} />
                <p className="text-muted fw-medium animate-pulse">Analyzing payroll data for {selectedMonth}...</p>
              </div>
            ) : error ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body p-5 text-center">
                  <div className="mb-4 text-warning">
                    <RiErrorWarningLine size={64} />
                  </div>
                  <h4 className="fw-bold mb-2">No Data Available</h4>
                  <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '400px' }}>
                    {error}
                  </p>
                  <div className="btn-outline-primary px-4 py-2 rounded-pill fw-bold" onClick={() => fetchSummary(selectedMonth)} style={{ cursor: 'pointer' }}>
                    Retry Connection
                  </div>
                </div>
              </div>
            ) : summaryData ? (
              <div className="position-relative">
                {/* PDF-Only Container */}
                <div
                  ref={reportRef}
                  className="bg-white p-5"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: '0',
                    width: '750px',
                    color: '#1a1a1a',
                    fontFamily: 'Arial, sans-serif',
                    display: 'none'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-30deg)',
                      opacity: '0.04',
                      zIndex: '0',
                      pointerEvents: 'none',
                      width: '80%',
                      textAlign: 'center'
                    }}
                  >
                    <img
                      src="/assets/images/burray/burray.png"
                      alt="Watermark"
                      style={{ width: '500px', height: 'auto' }}
                    />
                  </div>

                  <div className="text-center mb-5 pb-3 border-bottom position-relative" style={{ zIndex: '1' }}>
                    <h1 className="fw-bold mb-1" style={{ fontSize: '23px', color: '#0d6efd' }}>{company_name || 'Burray HR'}</h1>
                    <h1 className="fw-bold mb-1" style={{ fontSize: '16px', color: '#0d6efd' }}>Payroll Summary Report</h1>
                    <p className="small text-muted mb-0">Confidential • Generated: {new Date().toLocaleString()}</p>
                  </div>

                  <table className="table table-bordered align-middle mb-5 position-relative" style={{ fontSize: '13px', borderColor: '#dee2e6', zIndex: '1' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr className="text-uppercase fw-bold text-muted" style={{ letterSpacing: '0.05em' }}>
                        <th className="py-3 px-3">Payroll Metric</th>
                        <th className="py-3 px-3 text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Total Payroll Cost', key: 'total_payroll_cost', bold: true },
                        { label: 'Total Basic Salaries', key: 'total_basic_salaries' },
                        { label: 'Total Allowances', key: 'total_allowances' },
                        { label: 'Total NSSA', key: 'total_nssa' },
                        { label: 'Total PAYE', key: 'total_paye' },
                        { label: 'Total AIDS Levy', key: 'total_aids_levy' },
                        { label: 'Total Deductions', key: 'total_deductions' },
                        { label: 'Average Salary', key: 'average_salary' },
                        { label: 'NET PAY TOTAL', key: 'net_pay_total', bold: true, highlight: true }
                      ].map((item, idx) => {
                        const stats = summaryData?.stats || {}
                        const thisMonthVal = stats[item.key] || 0
                        return (
                          <tr key={idx} className={item.bold ? 'fw-bold' : ''}>
                            <td className={`py-4 px-3 ${item.highlight ? 'fs-5' : ''}`}>{item.label}</td>
                            <td className={`py-4 px-3 text-end ${item.highlight ? 'fs-5 text-success' : ''}`}>
                              {formatCurrency(thisMonthVal)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  <div className="mt-5 pt-5 text-center">
                    <h5 className="mt-2 fw-bold text-primary" style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontStyle: 'italic' }}>Burray Payroll Software</h5>
                    <div className="mx-auto border-top mt-1" style={{ width: '200px' }}></div>
                  </div>
                </div>

                {/* Dashboard View */}
                <div className="row mb-5">
                  <div className="col-lg-4 col-md-6 mb-4">
                    <SummaryCard
                      title="Total Payroll Cost"
                      value={summaryData?.stats?.total_payroll_cost || 0}
                      comparison={summaryData?.comparison?.total_payroll_cost || 0}
                      icon={RiMoneyDollarCircleLine}
                      color="primary"
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 mb-4">
                    <SummaryCard
                      title="Net Pay Total"
                      value={summaryData?.stats?.net_pay_total || 0}
                      comparison={summaryData?.comparison?.net_pay_total || 0}
                      icon={RiWallet3Line}
                      color="success"
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 mb-4">
                    <SummaryCard
                      title="Average Net Salary"
                      value={summaryData?.stats?.average_salary || 0}
                      comparison={summaryData?.comparison?.average_salary || 0}
                      icon={RiBankCardLine}
                      color="info"
                    />
                  </div>

                  <div className="col-12 mt-2 mb-3">
                    <h5 className="fw-bold text-muted text-uppercase small ls-wider">Earnings & Allowances</h5>
                  </div>

                  <div className="col-lg-6 col-md-6 mb-4">
                    <SummaryCard
                      title="Total Basic Salaries"
                      value={summaryData?.stats?.total_basic_salaries || 0}
                      comparison={summaryData?.comparison?.total_basic_salaries || 0}
                      icon={RiMoneyDollarCircleLine}
                      color="dark"
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 mb-4">
                    <SummaryCard
                      title="Total Allowances"
                      value={summaryData?.stats?.total_allowances || 0}
                      comparison={summaryData?.comparison?.total_allowances || 0}
                      icon={RiArrowRightSLine}
                      color="primary"
                    />
                  </div>

                  <div className="col-12 mt-4 mb-3">
                    <h5 className="fw-bold text-muted text-uppercase small ls-wider">Deductions & Statutories</h5>
                  </div>

                  <div className="col-lg-3 col-md-6 mb-4">
                    <SummaryCard
                      title="Total NSSA"
                      value={summaryData?.stats?.total_nssa || 0}
                      comparison={summaryData?.comparison?.total_nssa || 0}
                      icon={RiPercentLine}
                      color="warning"
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 mb-4">
                    <SummaryCard
                      title="Total PAYE"
                      value={summaryData?.stats?.total_paye || 0}
                      comparison={summaryData?.comparison?.total_paye || 0}
                      icon={RiPercentLine}
                      color="danger"
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 mb-4">
                    <SummaryCard
                      title="Total AIDS Levy"
                      value={summaryData?.stats?.total_aids_levy || 0}
                      comparison={summaryData?.comparison?.total_aids_levy || 0}
                      icon={RiPercentLine}
                      color="danger"
                    />
                  </div>
                  <div className="col-lg-3 col-md-6 mb-4">
                    <SummaryCard
                      title="Total Deductions"
                      value={summaryData?.stats?.total_deductions || 0}
                      comparison={summaryData?.comparison?.total_deductions || 0}
                      icon={RiArrowDownSLine}
                      color="secondary"
                    />
                  </div>
                </div>

                <div className="row mt-5">
                  <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                      <div className="d-flex align-items-center gap-2">
                        <div className="p-2 rounded bg-primary-subtle text-primary">
                          <RiFileList3Line size={24} />
                        </div>
                        <div>
                          <h2 className="fw-bold mb-0">Payroll Schedules</h2>
                          <p className="text-muted mb-0">Detailed breakdown of payroll components per employee</p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center bg-white border rounded-3 p-1 shadow-sm" style={{ height: '48px' }}>
                          <select
                            className="form-select border-0 shadow-none py-2 px-3"
                            value={selectedRunId}
                            onChange={(e) => setSelectedRunId(e.target.value)}
                            style={{ width: '220px', cursor: 'pointer', background: 'transparent' }}
                          >
                            <option value="">Select Payroll Run</option>
                            {payrollRuns.map(run => (
                              <option key={run.id} value={run.id}>
                                {new Date(run.month).toLocaleString('en-US', { month: 'long', year: 'numeric' })} - {new Date(run.date_processed || run.created_at).toLocaleDateString()}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-success d-flex align-items-center gap-2 px-3 shadow-sm"
                            onClick={handleExportExcel}
                            disabled={payslips.length === 0}
                            style={{ borderRadius: '10px', height: '48px' }}
                          >
                            <RiFileExcel2Line size={20} />
                            <span className="fw-bold d-none d-sm-inline">Excel</span>
                          </button>
                          <button
                            className="btn btn-outline-danger d-flex align-items-center gap-2 px-3 shadow-sm"
                            onClick={handleExportTablePDF}
                            disabled={payslips.length === 0}
                            style={{ borderRadius: '10px', height: '48px' }}
                          >
                            <RiFilePdfLine size={20} />
                            <span className="fw-bold d-none d-sm-inline">PDF</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-white border-bottom-0 pt-4 px-4">
                        <div className="d-flex flex-wrap gap-2">
                          {tabs.map(tab => (
                            <button
                              key={tab.id}
                              className={`btn btn-sm px-3 py-2 fw-medium ${activeTab === tab.id ? 'btn-primary shadow-sm' : 'btn-light text-muted'}`}
                              onClick={() => setActiveTab(tab.id)}
                              style={{ borderRadius: '8px' }}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="card-body p-0">
                        {renderTable()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
                @media print {
                    .d-print-block { display: block !important; }
                }
                .app-container {
                    background-color: #f8f9fa;
                    min-height: 100vh;
                }
                .spinner {
                    animation: rotate 2s linear infinite;
                }
                @keyframes rotate {
                    100% { transform: rotate(360deg); }
                }
                .animate-pulse {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: .5; }
                }
                .ls-wider {
                    letter-spacing: 0.1em;
                }
                .bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1) !important; }
                .bg-success-subtle { background-color: rgba(25, 135, 84, 0.1) !important; }
                .bg-info-subtle { background-color: rgba(13, 202, 240, 0.1) !important; }
                .bg-warning-subtle { background-color: rgba(255, 193, 7, 0.1) !important; }
                .bg-danger-subtle { background-color: rgba(220, 53, 69, 0.1) !important; }
                .bg-secondary-subtle { background-color: rgba(108, 117, 125, 0.1) !important; }
                .bg-dark-subtle { background-color: rgba(33, 37, 41, 0.1) !important; }
                
                .text-primary { color: #0d6efd !important; }
                .text-success { color: #198754 !important; }
                .text-info { color: #0dcaf0 !important; }
                .text-warning { color: #ffc107 !important; }
                .text-danger { color: #dc3545 !important; }
            `}</style>
    </div>
  )
}

export default Reports